import {
    IOutgoingRequestEvent, IOutgoingRequestParams,
    IOutgoingRequestPartB, IOutgoingRequestPartC
} from '@mecontrol/common';
import { ME, getPerfEntries, assign } from '../../utilities';
import { EventMoment } from '../eventQueue';
import { createPartA, getPageTags } from '../eventGenerator';
import { scrubUrl } from '../utilities';

const eventName = 'Ms.Webi.OutgoingRequest';
const eventBaseType = 'Ms.Qos.OutgoingServiceRequest';
// Filter all query parameters to be safe
// const c_queryFilters = ['login_hint', 'username'];

/**
 * Creates an OutgoingRequest event to capture information on calls to external services/servers
 * @param requestParams The outgoing request event's parameters
 * @returns The created OutgoingRequest event
 */
export function createOutgoingRequest(requestParams: IOutgoingRequestParams, moment: EventMoment): IOutgoingRequestEvent {

    // Try to retrieve performance API entry for the URL
    let entry = getEntry(requestParams.url);

    // Compute latency to report on the event
    // We will always prefer the performance API measurements and only
    // resort to our own measurements in their absence
    let latencyMs = Math.round(getEntryDuration(entry) || requestParams.duration);

    // Determine the operation name by extracting the last part of the path in the
    // requests URL. If there is a performance entry for the request, we get the URL
    // from there. Otherwise, we use the URL from the params.
    // Finally, if we can't find a good path part, we just reuse the service operation
    let operationName = getLastPathPart(entry ? entry.name : requestParams.url) ||
        requestParams.serviceOperation;

    let partA = createPartA(eventName, moment.date, moment.cv);

    let partB: IOutgoingRequestPartB = {
        operationName,
        dependencyOperationName: requestParams.serviceOperation,
        dependencyName: requestParams.service,
        latencyMs,
        succeeded: requestParams.success,
        targetUri: scrubUrl(requestParams.url)
    };

    let partC: IOutgoingRequestPartC = {
        baseType: eventBaseType,
        message: requestParams.message,
        pageName: moment.pageName,
        impressionGuid: moment.impressionGuid,
        market: ME.Config.mkt,
        customData: JSON.stringify(assign(
            {},
            {
                computedDuration: requestParams.duration || -1,
                perfDuration: getEntryDuration(entry) || -1
            },
            getPageTags(moment.accounts.counts, moment.accounts.current)
        ))
    };

    return {
        partA,
        partB,
        partC
    };
}

/**
 * Get a performance entry by looking for a particular name
 * @param name Entry name or partial name (URL)
 */
function getEntry(name: string): PerformanceEntry | undefined {
    const entries = getPerfEntries();
    for (let entry of entries) {
        if (entry.name === name || entry.name.indexOf(name) !== -1) {
            return entry;
        }
    }

    return undefined;
}

/**
 * Retrieve a possible performance entry duration value or (if not available)
 * compute one
 * @param entry PerformanceEntry to get the duration information from
 */
function getEntryDuration(entry: PerformanceEntry | undefined): number | undefined {
    if (entry) {
        switch (entry.entryType) {
            // Special case navigation entries (like our Webview html) that won't
            // have a complete entry until EVERYTHING completes loading.
            // We care about the network time here
            case 'navigation':
                return (entry as PerformanceNavigationTiming).responseEnd - entry.startTime;

            case 'resource':
            default:
                return entry.duration;

        }
    }

    return undefined;
}

/**
 * Get the last part of the path of URL
 * @param url URL to extract the last part of the path from
 */
function getLastPathPart(url?: string): string | undefined {
    if (url) {
        // Create an anchor tag to have it parse the URL
        let anchor = document.createElement('a');
        anchor.href = url;

        let path = anchor.pathname;
        let segments = path.split('/');
        for (let i = segments.length - 1; i >= 0; i--) {
            if (segments[i]) {
                return segments[i];
            }
        }
    }

    return;
}
